import React, { useState } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useLanguage } from '../LanguageContext';

const FeedbackForm = ({ onSuccess }) => {
    const { t } = useLanguage();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        smartphone: '',
        feedback: '',
    });

    // States für Datei und Status
    const [fileName, setFileName] = useState('');
    const [selectedFile, setSelectedFile] = useState(null); // Speichert das Datei-Objekt
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    const handleFileChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            setFileName(file.name);
            setSelectedFile(file); // WICHTIG: Datei im State speichern
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitDisabled) return;

        setIsSubmitting(true);
        setError(null);

        // FormData erstellen für Datei-Upload
        const data = new FormData();
        data.append('name', formData.name);
        data.append('email', formData.email);
        data.append('smartphone', formData.smartphone);
        data.append('feedback', formData.feedback);

        // Datei nur anhängen, wenn eine ausgewählt wurde
        if (selectedFile) {
            data.append('attachment', selectedFile);
        }

        const middlewareUrl = '/api/beta-feedback';

        try {
            const response = await fetch(middlewareUrl, {
                method: 'POST',
                body: data,
                // WICHTIG: KEINEN 'Content-Type' Header manuell setzen!
                // Der Browser macht das bei FormData automatisch richtig (multipart/form-data).
            });

            if (!response.ok) {
                throw new Error('Fehler beim Senden');
            }

            onSuccess();
        } catch (err) {
            console.error('Feedback Error:', err);
            setError(t('feedbackForm_error') || 'Ein Fehler ist aufgetreten.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const isSubmitDisabled = !formData.name || !formData.email || !formData.feedback || isSubmitting;

    return (
        <>
            <Typography variant="h4" component="h1" gutterBottom align="center">
                Feedback
            </Typography>
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 1 }}>
                Du kannst uns hier direkt Feedback geben, wenn du Teilnehmer des Beta Programms bist.
            </Typography>


            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
                <TextField margin="normal" required fullWidth id="name" label={t('feedbackForm_name')} name="name" value={formData.name} onChange={handleChange} disabled={isSubmitting} />
                <TextField margin="normal" required fullWidth id="email" label={t('feedbackForm_email')} name="email" type="email" value={formData.email} onChange={handleChange} disabled={isSubmitting} />

                <FormControl fullWidth margin="normal" required>
                    <InputLabel id="smartphone-select-label">{t('feedbackForm_smartphone')}</InputLabel>
                    <Select
                        labelId="smartphone-select-label"
                        id="smartphone"
                        name="smartphone"
                        value={formData.smartphone}
                        label={t('feedbackForm_smartphone')}
                        onChange={handleChange}
                        disabled={isSubmitting}
                    >
                        <MenuItem value="ios">iOS</MenuItem>
                        <MenuItem value="android">Android</MenuItem>
                    </Select>
                </FormControl>

                <Typography variant="body1" align="center" color="text.secondary" sx={{ mt: 4 }}>
                    Bitte versuche dein Problem oder Anliegen so <b>genau wie möglich</b> zu beschreiben.
                </Typography>

                <TextField margin="normal" required fullWidth multiline rows={5} id="feedback" label={t('feedbackForm_feedback')} name="feedback" value={formData.feedback} onChange={handleChange} disabled={isSubmitting} />

                <Button
                    component="label"
                    role={undefined}
                    variant="outlined"
                    tabIndex={-1}
                    startIcon={<CloudUploadIcon />}
                    sx={{ mt: 2, width: '100%' }}
                    disabled={isSubmitting}
                >
                    {t('feedbackForm_upload')}
                    <input type="file" hidden onChange={handleFileChange} accept="image/*,.log,.txt,.pdf" />
                </Button>
                {fileName && <Typography variant="body2" color="text.secondary" sx={{mt: 1, textAlign: 'center'}}>{fileName}</Typography>}

                {error && <Typography color="error" align="center" sx={{ mt: 2 }}>{error}</Typography>}

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    disabled={isSubmitDisabled}
                    sx={{ mt: 3, mb: 2, fontSize: '1rem' }}
                >
                    {isSubmitting ? "Wird gesendet..." : t('feedbackForm_submit')}
                </Button>
            </Box>
        </>
    );
};

export default FeedbackForm;